import React from "react";
import { colors, fonts, spacings, fontSizes } from "@ui/Theme";
import InputV2 from "@ui/Input/InputV2";
import ErrorWarningLine from "@ui/IconV2/react/ErrorWarningLine";
import { css } from "@linaria/core";
import cx from "classnames";

interface Props {
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  error?: React.ReactNode;
  className?: string;
  label?: string;
  type?: React.HTMLInputTypeAttribute;
  value?: string | number | readonly string[];
}
// This is really only meant to be used once. If you have to use it multiple times, then use some uuid setup.
const htmlId = "small-input";

// The reason for this code: InputV2 does not allow for changing the input's size while keeping the label and error message at full width.
// I tried to use the InputV2 interface but the notion of a label and error message an input cannot be treated seperately.

// This is a crappy workaround to make the input small without hacking up too much InputV2's internals.
// Any significant change or re-usage of this component should merit a look into putting something in the design system directly.
export const SmallInput = ({ onChange, error, className, type, label, value }: Props) => (
  <div>
    {label && (
      <label htmlFor={htmlId} className={labelCss}>
        {label}
      </label>
    )}
    <InputV2
      id={htmlId}
      className={cx(className, inputCss)}
      type={type}
      style={error ? errorStyle : undefined}
      onChange={onChange}
      value={value}
    />
    {error && (
      <p className={fieldErrorCss}>
        <ErrorWarningLine size="s" />
        <span>{error}</span>
      </p>
    )}
  </div>
);

const inputCss = css`
  margin-bottom: ${spacings.xxs};
`;
const errorStyle = {
  color: `${colors.carbon900}`,
  borderColor: `${colors.oxygen400}`,
};

const labelCss = css`
  display: block;
  color: var(--carbon800);
  font-size: ${fontSizes[4]};
  font-weight: 600;
  margin-bottom: ${spacings.xxs};
`;

const fieldErrorCss = css`
  color: ${colors.oxygen400};
  font: ${fonts.body2};
  text-align: left;
  display: flex;
  gap: 4px;

  > :first-child {
    flex-shrink: 0;
    height: 18px;
  }
`;
